import { signal, computed } from '@angular/core';
import { HeaderMode } from '../table-header/enums/HeaderMode';
import { GridState, GridStateActions, GridEvent, GridNotification, NotificationData } from './states/grid-state.interface';
import { IdleState } from './states/idle-state';
import { SelectionState } from './states/selection-state';
import { EditState } from './states/edit-state';
import { DuplicationState } from './states/duplication-state';

/**
 * Context for the State pattern implementation.
 * Delegates state-specific behavior to concrete state objects.
 *
 * State transitions:
 * - Idle -> Selection (when rows are selected)
 * - Idle -> Edit (when inline editing starts)
 * - Idle -> Duplication (when duplicating rows)
 * - Selection -> Duplication (when duplicating selected rows)
 * - Selection -> Idle (when deselecting)
 * - Duplication -> Idle (when saving or canceling)
 * - Edit -> Idle (when saving or canceling)
 * - Edit -> Selection (when selecting rows while editing)
 */
export class GridStateMachine {
  private _currentState: GridState;
  private _currentMode = signal<HeaderMode>(HeaderMode.Idle);
  private actions!: GridStateActions;

  /** Map of all possible states */
  private states = new Map<HeaderMode, GridState>([
    [HeaderMode.Idle, new IdleState()],
    [HeaderMode.Selection, new SelectionState()],
    [HeaderMode.Edit, new EditState()],
    [HeaderMode.Duplication, new DuplicationState()],
  ]);

  constructor() {
    // Initialize with Idle state
    this._currentState = this.states.get(HeaderMode.Idle)!;
  }

  /**
   * Register the actions that states can execute
   * This should be called once during initialization
   */
  setActions(actions: GridStateActions): void {
    this.actions = actions;
  }

  /** The current state mode */
  currentState = computed(() => this._currentMode());

  /**
   * Transition to a new state based on an event
   * Delegates to the current state to determine if transition is allowed
   * @param event The event that triggers the transition
   */
  transition(event: GridEvent): void {
    const nextMode = this._currentState.canTransitionTo(event);

    if (nextMode !== null) {
      // Execute onExit for current state
      this._currentState.onExit(this.actions, event);

      // Transition to next state
      const nextState = this.states.get(nextMode)!;
      this._currentState = nextState;
      this._currentMode.set(nextMode);

      // Execute onEnter for next state
      this._currentState.onEnter(this.actions);
    }
  }

  /**
   * Notify the current state about something that happened in the context
   * The state decides whether to react to this notification
   * @param notification The type of notification
   * @param data Additional data with the notification
   */
  notify(notification: GridNotification, data: NotificationData): void {
    const event = this._currentState.handleNotification(notification, data, this.actions);

    if (event !== null) {
      this.transition(event);
    }
  }

  /**
   * Get the data that should be saved in the current state
   * Delegates to the current state to decide what data to return
   * @param context Data available in the context
   * @returns The data to save
   */
  getDataToSave(context: { duplicatedRows: any[]; editedRows: Map<number, any> }): any[] {
    return this._currentState.getDataToSave(context);
  }

  /**
   * Reset the state machine to idle
   */
  reset(): void {
    this._currentState = this.states.get(HeaderMode.Idle)!;
    this._currentMode.set(HeaderMode.Idle);
  }
}
